import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { useTranslation } from "react-i18next";

interface TokenInfo {
  used: number;
  max: number;
  percentage: number;
  expireAt: string;
  isLimitReached: boolean;
  isExpired: boolean;
  quota_type: "tokens" | "questions";
}

export default function TokenProgressCircle({ refreshTrigger }: { refreshTrigger: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState<TokenInfo | null>(null);

  // 🔄 Re-fetch when refreshTrigger changes
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<TokenInfo>("/user/token-percentage");
        setData(res.data);
      } catch (err) {
        console.error("Erreur récupération tokens", err);
      }
    })();
  }, [refreshTrigger]); // 👈 key line !!!

  if (!data) return null;

  const radius = 60;
  const stroke = 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (data.percentage / 100) * circumference;

  const getStrokeColor = () => {
    if (data.percentage <= 25) return "#333";
    if (data.percentage <= 50) return "#4CAF50";
    if (data.percentage <= 75) return "#ff9800";
    return "#f44336";
  };

  const strokeColor = getStrokeColor();

  const expireDate = new Date(data.expireAt).toLocaleDateString("fr-FR");

  return (
    <div className="token-progress-container" style={{ textAlign: "center", marginBottom: 10 }}>
      {/* Compact mobile layout - circle only, text on hover */}
      <div className="d-md-none d-flex flex-column align-items-center justify-content-center token-mobile-wrapper">
        <div className="token-circle-hover" style={{ position: 'relative' }}>
          {data.quota_type === "questions" ? (
            <div style={{
              width: "55px",
              height: "55px",
              borderRadius: "50%",
              border: "2px solid rgba(255, 255, 255, 0.2)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255, 255, 255, 0.05)"
            }}>
              <span style={{ fontSize: "14px", fontWeight: "bold", color: "white" }}>{data.max - data.used}</span>
              <span style={{ fontSize: "7px", color: "rgba(255, 255, 255, 0.7)", textTransform: "uppercase" }}>Q.</span>
            </div>
          ) : (
            <svg width="55" height="55">
              <circle
                cx="27.5"
                cy="27.5"
                r="22"
                stroke="#666666ff"
                strokeWidth={2}
                fill="none"
              />
              <circle
                cx="27.5"
                cy="27.5"
                r="22"
                stroke={strokeColor}
                strokeWidth={2}
                fill="none"
                strokeDasharray={2 * Math.PI * 22}
                strokeDashoffset={(2 * Math.PI * 22) - ((data.percentage / 100) * 2 * Math.PI * 22)}
                style={{ transition: "stroke-dashoffset 0.6s ease" }}
                strokeLinecap="butt"
              />
              <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dy="4px"
                fontSize="12"
                fontWeight="bold"
                fill="white"
              >
                {data.percentage}%
              </text>
            </svg>
          )}
          {/* Tooltip on hover */}
          <div className="token-hover-text">
            {data.quota_type === "questions"
              ? `${data.max - data.used} ${t("progress.questions_left", "questions rest.")} • ${expireDate}`
              : `${100 - data.percentage}% ${t("progress.left")} • ${expireDate}`}
            {data.isLimitReached && <span className="text-danger d-block">{t("progress.expired")}</span>}
          </div>
        </div>
      </div>

      {/* Desktop layout */}
      <div className="d-none d-md-block">
        {data.quota_type === "questions" ? (
          <div className="token-questions-display" style={{
            padding: "20px",
            background: "rgba(255, 255, 255, 0.05)",
            borderRadius: "15px",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            marginBottom: "15px"
          }}>
            <div style={{ fontSize: "28px", fontWeight: "bold", color: "white" }}>
              {data.max - data.used}
            </div>
            <div style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.7)", textTransform: "uppercase", letterSpacing: "1px" }}>
              {t("progress.questions_left", "Questions restantes")}
            </div>
            <div style={{ marginTop: "10px", fontSize: "14px", color: "white" }}>
              {t("progress.of", "sur")} <strong>{data.max}</strong>
            </div>
          </div>
        ) : (
          <svg width="150" height="150">
            <circle
              cx="75"
              cy="75"
              r={radius}
              stroke="white"
              strokeWidth={stroke}
              fill="none"
            />
            <circle
              cx="75"
              cy="75"
              r={radius}
              stroke={strokeColor}
              strokeWidth={stroke}
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={circumference - progress}
              style={{ transition: "stroke-dashoffset 0.6s ease" }}
              strokeLinecap="butt"
            />
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dy="8px"
              fontSize="22"
              fontWeight="bold"
              fill="white"
            >
              {data.percentage}%
            </text>
          </svg>
        )}

        <p style={{ color: "white", marginTop: 8, fontSize: 12 }}>
          {data.quota_type === "tokens" ? (
            <>
              {t("progress.left")} {100 - data.percentage}% {t("progress.unteal")} : <strong>{expireDate}</strong>
            </>
          ) : (
            <>
              {t("progress.valid_until", "Valide jusqu'au")} : <strong>{expireDate}</strong>
            </>
          )}
        </p>

        {data.isLimitReached && (
          <div style={{ marginTop: 8 }}>
            <p style={{ color: "red", fontWeight: "bold", marginBottom: 4 }}>
              {t("progress.expired")}
            </p>
            <button
              className="btn btn-danger btn-sm"
              style={{ fontSize: 12, padding: "2px 8px" }}
              onClick={() => navigate("/fr/dashboard/subscription")}
            >
              Mettre à niveau
            </button>
          </div>
        )}
      </div>

      {/* Limit reached warning - show on both mobile */}
      {data.isLimitReached && (
        <div className="d-md-none text-center" style={{ marginTop: 4 }}>
          <p style={{ color: "red", fontWeight: "bold", fontSize: "0.7rem", marginBottom: 2 }}>
            {t("progress.expired")}
          </p>
          <button
            className="btn btn-danger btn-sm"
            style={{ fontSize: 10, padding: "1px 6px" }}
            onClick={() => navigate("/fr/dashboard/subscription")}
          >
            Upgrade
          </button>
        </div>
      )}
    </div>
  );
}
